'use client';

/**
 * useCustomerInfos - Hook to fetch customer information (v2)
 * Fetches customer infos from `/api/v1/customers/{customerId}` with `storeId` param
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CustomerInfos, CustomerResource } from '../../core/resources/customer';
import { useTagadaContext } from '../providers/TagadaProvider';
import { getGlobalApiClient } from './useApiQuery';
import { usePluginConfig } from './usePluginConfig';

export interface UseCustomerInfosOptions {
    customerId?: string | null;
    enabled?: boolean;
}

export interface UseCustomerInfosResult {
    data: CustomerInfos | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

export function useCustomerInfos(options: UseCustomerInfosOptions = {}): UseCustomerInfosResult {
    const { storeId } = usePluginConfig();
    const { isSessionInitialized } = useTagadaContext();

    const { customerId = null, enabled = true } = options;

    // Create customer resource client
    const customerResource = useMemo(() => {
        try {
            return new CustomerResource(getGlobalApiClient());
        } catch (error) {
            throw new Error(
                'Failed to initialize customer resource: ' +
                (error instanceof Error ? error.message : 'Unknown error')
            );
        }
    }, []);

    // Determine if query should be enabled
    const isEnabled = useMemo(() => {
        return Boolean(enabled && customerId && storeId && isSessionInitialized);
    }, [enabled, customerId, storeId, isSessionInitialized]);

    // Create query key
    const queryKey = useMemo(
        () => ['customer-infos', { customerId, storeId }],
        [customerId, storeId]
    );

    // Fetch customer infos using TanStack Query
    const {
        data = null,
        isLoading,
        error,
        refetch,
    } = useQuery<CustomerInfos | null>({
        queryKey,
        enabled: isEnabled,
        queryFn: async () => {
            if (!customerId || !storeId) {
                return null;
            }

            return await customerResource.getCustomerInfos(customerId, storeId);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });

    return {
        data,
        isLoading,
        error,
        refetch: async () => {
            await refetch();
        },
    };
}

